/**
 * PWA install state.
 * - Android/Chrome: capture beforeinstallprompt, trigger via promptInstall().
 * - iOS Safari: no prompt event exists; show manual Add-to-Home-Screen hint.
 * - In-app browsers (Chrome iOS, Firefox iOS, etc.) and standalone mode: nothing shown.
 *
 * Dismissal is persisted in localStorage. Once dismissed, banners stay hidden
 * for that browser until storage is cleared. The native browser install UI
 * (Chrome address-bar icon, iOS Share menu) remains available regardless.
 */
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const DISMISSED_PROMPT_KEY = 'ti:install-dismissed';
const DISMISSED_IOS_HINT_KEY = 'ti:install-hint-ios-dismissed';

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari pre-PWA: legacy navigator.standalone flag
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac; distinguish by touch support.
  return ua.includes('Mac') && navigator.maxTouchPoints > 1;
}

export function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  // Exclude in-app/third-party iOS browsers — they can't install PWAs.
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // localStorage blocked (private mode quota, etc.) — banner will reappear, acceptable.
  }
}

export interface InstallPromptState {
  canPrompt: boolean;
  promptInstall: () => Promise<void>;
  dismiss: () => void;
}

export function useInstallPrompt(): InstallPromptState {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() =>
    readFlag(DISMISSED_PROMPT_KEY),
  );

  useEffect(() => {
    if (isStandalone()) return;

    function onPrompt(e: Event): void {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    }
    function onInstalled(): void {
      setEvent(null);
    }

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function promptInstall(): Promise<void> {
    if (!event) return;
    try {
      await event.prompt();
      await event.userChoice;
    } finally {
      // The browser only allows one prompt per captured event. Drop it either way.
      setEvent(null);
    }
  }

  function dismiss(): void {
    writeFlag(DISMISSED_PROMPT_KEY);
    setDismissed(true);
    setEvent(null);
  }

  return {
    canPrompt: event !== null && !dismissed,
    promptInstall,
    dismiss,
  };
}

export interface IOSHintState {
  visible: boolean;
  dismiss: () => void;
}

export function useIOSInstallHint(): IOSHintState {
  const [dismissed, setDismissed] = useState<boolean>(() =>
    readFlag(DISMISSED_IOS_HINT_KEY),
  );
  const [eligible, setEligible] = useState<boolean>(false);

  useEffect(() => {
    if (isStandalone()) {
      setEligible(false);
      return;
    }
    setEligible(isIOSSafari());
  }, []);

  function dismiss(): void {
    writeFlag(DISMISSED_IOS_HINT_KEY);
    setDismissed(true);
  }

  return {
    visible: eligible && !dismissed,
    dismiss,
  };
}
